import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Save, Loader2, Plus, Trash2, AlertTriangle } from 'lucide-react';
import SavedContactCombobox from '@/components/SavedContactCombobox';
import EventTypeCombobox from '@/components/EventTypeCombobox';
import { EVENT_TYPES } from '@/config/eventTypes';

// Today as YYYY-MM-DD — used to constrain past-only date pickers.
const TODAY_ISO = (() => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
})();

const AddServiceRequestPage = () => {
  const navigate = useNavigate();
  const { api, currentFacility, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [repairSites, setRepairSites] = useState([]);
  const [savedContactKey, setSavedContactKey] = useState(0);
  const [savedRequestorKey, setSavedRequestorKey] = useState(0);
  const [savedBillingKey, setSavedBillingKey] = useState(0);
  // Rows 2..9 of the Railcar Identification section. Row 1 stays in formData.
  const [extraCars, setExtraCars] = useState([]); // [{ car_mark, car_number }]
  // Field-level errors for the railcar rows. Indexed 0..N where 0 = row 1.
  const [carErrors, setCarErrors] = useState({});
  // 2026-06-05 — Duplicate SR confirmation modal state (see handleSubmit).
  const [dupModalOpen, setDupModalOpen] = useState(false);
  const [dupMatch, setDupMatch] = useState(null);
  const isTechnician = user?.role === 'TECHNICIAN';
  const [formData, setFormData] = useState({
    // Railcar Identification (Top Section)
    car_mark: '',
    car_number: '',
    
    // Section 1: General
    // 2026-07-06 — Technicians default to ONSITE (field-created work) but the
    // field remains editable. All other roles start blank and must choose.
    service_type: user?.role === 'TECHNICIAN' ? 'ONSITE' : '',
    facility_id: currentFacility || '',
    // 2026-07-05 Wave 3 #4 — Default Call Date uses the user's LOCAL date
    // (TODAY_ISO above), not UTC. `new Date().toISOString()` would return the
    // UTC date, which rolls forward incorrectly in evening hours west of UTC.
    call_date: TODAY_ISO,
    
    // Section 2: Car Information
    car_owner: '',
    car_lessee: '',
    se_owner: '',
    car_type: '',
    car_type_other: '',
    lining_owner: '',
    lining_type: '',
    jacketed: false,
    insulation_type: '',
    interior_condition: 'CLEAN',
    interior_condition_other: '',
    last_contents: '',
    car_owner_contact: '',
    
    // Section 3: Scope of Work
    event_type: '',
    work_procedures: '',
    shopping_reason: '',
    
    // Section 4: Gasket Specifications
    gasket_manway_cover: '',
    gasket_flanged_valves: '',
    gasket_bov_flange: '',
    gasket_bov_cap_material: '',
    gasket_oring_spec: '',
    gasket_food_grade: false,
    
    // Section 5: Car Location
    site_name: '',
    // Hidden link to a Customers & Contacts repair-site row. Populated when
    // the user picks from the "Saved Repair Site" combobox; preserved across
    // subsequent text edits so the WS detail page can still resolve the
    // contact even if the visible site name is later tweaked.
    repair_site_id: '',
    splc_code: '',
    location_address: '',
    location_address_2: '',
    location_city: '',
    location_state: '',
    location_zip: '',
    site_contact: '',
    site_contact_number: '',
    site_contact_email: '',
    
    // Section 6: Service Requestor
    requestor_full_name: '',
    requestor_company: '',
    requestor_phone: '',
    requestor_email: '',
    
    // Section 7: Billing Party
    billing_company: '',
    billing_contact_name: '',
    billing_contact_email: '',
    billing_contact_phone: ''
  });

  const carTypes = [
    'General Purpose Tank Car',
    'High Pressure Tank Car',
    'Hopper Car',
    'Box Car',
    'Other Freight'
  ];

  // Event types imported from shared config (alphabetically sorted)
  const eventTypes = EVENT_TYPES;

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const response = await api.get('/facilities');
        setFacilities(response.data);
        if (response.data.length === 1 && !formData.facility_id) {
          setFormData(prev => ({ ...prev, facility_id: response.data[0].id }));
        }
      } catch (error) {
        console.error('Failed to fetch facilities:', error);
      }
    };
    fetchFacilities();
    
    const fetchRepairSites = async () => {
      try {
        const res = await api.get('/repair-sites');
        setRepairSites(res.data);
      } catch (err) { console.error('Failed to fetch repair sites:', err); }
    };
    fetchRepairSites();
  }, [api, formData.facility_id]);

  // 2026-08-19 — High Pressure Tank Car defaults Jacketed to Yes ONE TIME
  // on Car Type change. After that the user may override to No and their
  // choice persists (previously the effect fired on every jacketed change
  // and forced Yes back on).
  const prevCarTypeRef = useRef(formData.car_type);
  useEffect(() => {
    if (formData.car_type !== prevCarTypeRef.current) {
      if (formData.car_type === 'High Pressure Tank Car') {
        setFormData(prev => ({ ...prev, jacketed: true }));
      }
      prevCarTypeRef.current = formData.car_type;
    }
  }, [formData.car_type]);

  const handleRepairSiteSelect = (siteId) => {
    if (!siteId) return;
    // 2026-07-05 Wave 2: Dedicated Sites are selectable here too and use the
    // same autofill mapping as Repair Sites (same field set).
    const site = repairSites.find(s => s.id === siteId && ((s.contact_type || 'REPAIR_SITE') === 'REPAIR_SITE' || s.contact_type === 'DEDICATED_SITE'));
    if (!site) return;
    setFormData(prev => ({
      ...prev,
      repair_site_id: site.id,
      site_name: site.repair_site_name,
      splc_code: site.splc_code,
      location_address: site.address,
      location_address_2: site.address_line_2,
      location_city: site.city,
      location_state: site.state_province,
      location_zip: site.zip_code,
      site_contact: site.site_contact,
      site_contact_number: site.site_contact_number,
      site_contact_email: site.site_contact_email
    }));
    // Reset the Select to placeholder so user can re-pick another and see helper text.
    setSavedContactKey(k => k + 1);
  };

  const repairSiteContacts = repairSites.filter(c => {
    const t = c.contact_type || 'REPAIR_SITE';
    return t === 'REPAIR_SITE' || t === 'DEDICATED_SITE';
  });
  const requestorContacts = repairSites.filter(c => c.contact_type === 'SERVICE_REQUESTOR');
  const billingContacts = repairSites.filter(c => c.contact_type === 'RESPONSIBLE_BILLING_PARTY');

  const handleRequestorSelect = (contactId) => {
    if (!contactId) return;
    const c = requestorContacts.find(x => x.id === contactId);
    if (!c) return;
    setFormData(prev => ({
      ...prev,
      requestor_full_name: c.requestor_full_name || '',
      requestor_company: c.requestor_company || '',
      requestor_phone: c.requestor_phone || '',
      requestor_email: c.requestor_email || '',
    }));
    setSavedRequestorKey(k => k + 1);
  };

  const handleBillingSelect = (contactId) => {
    if (!contactId) return;
    const c = billingContacts.find(x => x.id === contactId);
    if (!c) return;
    setFormData(prev => ({
      ...prev,
      billing_company: c.billing_company || '',
      billing_contact_name: c.billing_contact_name || '',
      billing_contact_email: c.billing_contact_email || '',
      billing_contact_phone: c.billing_contact_phone || '',
    }));
    setSavedBillingKey(k => k + 1);
  };

  // ----- Batch car-row helpers (rows 2..9) -----
  const addExtraCar = () => {
    setExtraCars(prev => (prev.length >= 8 ? prev : [...prev, { car_mark: '', car_number: '' }]));
  };

  const removeExtraCar = (idx) => {
    setExtraCars(prev => prev.filter((_, i) => i !== idx));
    // Drop any errors for the removed row and re-index subsequent rows.
    setCarErrors(prev => {
      const next = { 0: prev[0] };
      Object.keys(prev).forEach(k => {
        const ki = parseInt(k, 10);
        if (ki === 0 || ki === idx + 1) return;
        const newIdx = ki > idx + 1 ? ki - 1 : ki;
        next[newIdx] = prev[ki];
      });
      return next;
    });
  };

  const updateExtraCar = (idx, field, value) => {
    setExtraCars(prev => prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
  };

  /** Validate every car row (row 1 + extras). Returns { errors, allCars }.
   *  - errors keyed by 0-based row index with { mark, number } messages.
   *  - allCars is the concrete list used for submission.
   *  - Within-batch duplicate detection: same (mark, number) pair in 2+ rows.
   */
  const validateCarRows = () => {
    const allCars = [
      { car_mark: formData.car_mark, car_number: formData.car_number },
      ...extraCars,
    ];
    const errors = {};
    allCars.forEach((row, i) => {
      const e = {};
      const mark = (row.car_mark || '').trim();
      const num = (row.car_number || '').trim();
      if (!mark) e.mark = 'Car Mark is required';
      else if (mark.length > 4) e.mark = 'Car Mark must be 1–4 letters';
      else if (!/^[A-Z]+$/.test(mark)) e.mark = 'Car Mark must be letters only';
      if (!num) e.number = 'Car Number is required';
      else if (num.length > 6) e.number = 'Car Number must be 1–6 digits';
      else if (!/^\d+$/.test(num)) e.number = 'Car Number must be digits only';
      if (Object.keys(e).length) errors[i] = e;
    });
    // Within-batch duplicate detection (Car Mark + Car Number identical)
    const seen = new Map(); // key -> first row index
    allCars.forEach((row, i) => {
      const mark = (row.car_mark || '').trim().toUpperCase();
      const num = (row.car_number || '').trim();
      if (!mark || !num) return;
      const key = `${mark} ${num}`;
      if (seen.has(key)) {
        const first = seen.get(key);
        const dupMsg = `Duplicate of ${key} (also in row ${first + 1})`;
        errors[i] = { ...(errors[i] || {}), mark: dupMsg, number: dupMsg };
        // Flag the first occurrence with a matching message so both rows are
        // visibly highlighted and the toast reads naturally.
        errors[first] = {
          ...(errors[first] || {}),
          mark: errors[first]?.mark || `Duplicate of ${key} (also in row ${i + 1})`,
          number: errors[first]?.number || `Duplicate of ${key} (also in row ${i + 1})`,
        };
      } else {
        seen.set(key, i);
      }
    });
    return { errors, allCars };
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ----- Car row validation (row 1 + extras + within-batch duplicates) -----
    const { errors, allCars } = validateCarRows();
    setCarErrors(errors);
    if (Object.keys(errors).length > 0) {
      // Try to message the user about the first specific row issue.
      const firstBadIdx = Object.keys(errors).map(Number).sort((a, b) => a - b)[0];
      const e0 = errors[firstBadIdx];
      const msg = e0?.mark || e0?.number || `Row ${firstBadIdx + 1} has a problem`;
      toast.error(allCars.length > 1 ? `Row ${firstBadIdx + 1}: ${msg}` : msg);
      return;
    }

    if (!formData.facility_id) {
      toast.error('Please select a facility');
      return;
    }
    if (!formData.service_type) {
      toast.error('Please select a service type');
      return;
    }
    // Block creation if the selected facility has no Facility ID yet.
    const selectedFacility = facilities.find(f => f.id === formData.facility_id);
    if (selectedFacility && !selectedFacility.facility_code) {
      toast.error(
        'A Facility ID is required before creating service requests. Please set your Facility ID in Facility Management.'
      );
      return;
    }
    if (!formData.car_owner) {
      toast.error('Please enter the car owner');
      return;
    }
    if (!formData.car_type) {
      toast.error('Please select a car type');
      return;
    }
    if (!formData.car_owner_contact) {
      toast.error('Please enter the car owner contact email');
      return;
    }
    if (!formData.event_type) {
      toast.error('Please select an event type');
      return;
    }
    if (!formData.site_name) {
      toast.error('Please enter the site name');
      return;
    }
    if (!formData.requestor_company) {
      toast.error('Please enter the requestor company');
      return;
    }

    // 2026-06-05 — Duplicate SR check (per-facility, open statuses only).
    // Fires only for single-car submissions; multi-car batches skip the modal
    // because the "which car matched?" UX would be ambiguous.
    if (allCars.length === 1) {
      try {
        const dup = await api.post('/service-requests/check-duplicate', {
          facility_id: formData.facility_id,
          car_mark: allCars[0].car_mark,
          car_number: allCars[0].car_number,
          event_type: formData.event_type,
          site_name: formData.site_name,
        });
        if (dup?.data?.match) {
          setDupMatch(dup.data.match);
          setDupModalOpen(true);
          return;
        }
      } catch (err) {
        // Non-blocking — if the check itself fails, fall through to create.
      }
    }

    await performCreate(allCars);
  };

  const performCreate = async (allCars) => {
    setDupModalOpen(false);
    setLoading(true);
    try {
      const commonPayload = Object.fromEntries(
        Object.entries(formData).map(([key, value]) => {
          if (key === 'car_mark' || key === 'car_number') return [key, value];
          if (value === '') return [key, null];
          return [key, value];
        })
      );

      if (allCars.length === 1) {
        const response = await api.post('/service-requests', { ...commonPayload, ...allCars[0] });
        toast.success(`Service Request ${response.data.job_number} created successfully!`);
        navigate('/service-requests');
        return;
      }

      // Batch path: create one SR per row sequentially using the same
      // /service-requests endpoint so SR numbering and business rules are
      // identical to the single-car path. Backend MongoDB does not run as a
      // replica set locally, so we cannot wrap this in a transaction; the
      // creation is sequential and best-effort. If any row fails mid-batch,
      // we surface counts back to the user and keep the form intact so they
      // can retry the remaining rows.
      const created = [];
      let failure = null;
      for (let i = 0; i < allCars.length; i++) {
        try {
          const resp = await api.post('/service-requests', { ...commonPayload, ...allCars[i] });
          created.push(resp.data.job_number);
        } catch (err) {
          failure = { idx: i, err };
          break;
        }
      }

      if (failure) {
        const detail = failure.err.response?.data?.detail;
        const msg = Array.isArray(detail)
          ? detail.map(d => `${d.loc?.slice(-1)[0] || 'Field'}: ${d.msg}`).join(', ')
          : (detail || 'Service request creation failed');
        if (created.length === 0) {
          toast.error(`No service requests were created. ${msg}`);
        } else {
          toast.error(
            `${created.length} of ${allCars.length} service requests created. Row ${failure.idx + 1} failed: ${msg}`
          );
        }
        return; // Keep form intact for retry.
      }

      toast.success(`${created.length} service requests created successfully.`);
      navigate('/service-requests');
    } catch (error) {
      console.error('Service request creation error:', error.response?.data);
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const messages = detail.map(err => `${err.loc?.slice(-1)[0] || 'Field'}: ${err.msg}`).join(', ');
        toast.error(messages);
      } else {
        toast.error(detail || 'Failed to create service request');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="add-service-request-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/service-requests')}
          className="p-2"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-[#323033]">Railcar Service Request Form</h1>
          <p className="text-[#5A5A5A]">Create a new service request</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* RAILCAR IDENTIFICATION - Prominent Top Section */}
        <div 
          className="bg-[#FAFAFA] border-2 border-[#D3D5D4] rounded-xl p-6 shadow-sm"
          data-testid="railcar-identification-section"
        >
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold text-[#323033] uppercase tracking-wide">
              Railcar Identification
            </h2>
            <span className="text-xs font-medium text-[#5A5A5A]" data-testid="car-count-indicator">
              {1 + extraCars.length} of 9 cars
            </span>
          </div>

          {/* Row 1 — primary car (always present, cannot be removed) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="car_mark" className="text-base font-medium">Car Mark *</Label>
              <Input
                id="car_mark"
                value={formData.car_mark}
                onChange={(e) => handleChange('car_mark', e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))}
                placeholder="ABCX"
                maxLength={4}
                required
                className={`text-lg font-medium h-12 border-2 ${carErrors[0]?.mark ? 'border-red-500' : 'border-[#D3D5D4]'} focus:border-[#005EF8] focus:ring-[#005EF8]/10`}
                data-testid="car-mark-input"
              />
              {carErrors[0]?.mark && <p className="text-xs text-red-600">{carErrors[0].mark}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="car_number" className="text-base font-medium">Car Number *</Label>
              <Input
                id="car_number"
                value={formData.car_number}
                onChange={(e) => handleChange('car_number', e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                maxLength={6}
                required
                className={`text-lg font-medium h-12 border-2 ${carErrors[0]?.number ? 'border-red-500' : 'border-[#D3D5D4]'} focus:border-[#005EF8] focus:ring-[#005EF8]/10`}
                data-testid="car-number-input"
              />
              {carErrors[0]?.number && <p className="text-xs text-red-600">{carErrors[0].number}</p>}
            </div>
          </div>

          {/* Additional car rows (2..9) */}
          {extraCars.map((row, idx) => {
            const rowIdx = idx + 1; // 1-based index into carErrors
            const errs = carErrors[rowIdx] || {};
            return (
              <div key={`extra-car-${idx}`} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-6 mt-4" data-testid={`extra-car-row-${idx}`}>
                <div className="space-y-2">
                  <Label className="text-base font-medium">Car Mark *</Label>
                  <Input
                    value={row.car_mark}
                    onChange={(e) => updateExtraCar(idx, 'car_mark', e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))}
                    placeholder="ABCX"
                    maxLength={4}
                    className={`text-lg font-medium h-12 border-2 ${errs.mark ? 'border-red-500' : 'border-[#D3D5D4]'} focus:border-[#005EF8] focus:ring-[#005EF8]/10`}
                    data-testid={`extra-car-mark-${idx}`}
                  />
                  {errs.mark && <p className="text-xs text-red-600">{errs.mark}</p>}
                </div>
                <div className="space-y-2">
                  <Label className="text-base font-medium">Car Number *</Label>
                  <Input
                    value={row.car_number}
                    onChange={(e) => updateExtraCar(idx, 'car_number', e.target.value.replace(/\D/g, ''))}
                    placeholder="123456"
                    maxLength={6}
                    className={`text-lg font-medium h-12 border-2 ${errs.number ? 'border-red-500' : 'border-[#D3D5D4]'} focus:border-[#005EF8] focus:ring-[#005EF8]/10`}
                    data-testid={`extra-car-number-${idx}`}
                  />
                  {errs.number && <p className="text-xs text-red-600">{errs.number}</p>}
                </div>
                <div className="flex items-end pb-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeExtraCar(idx)}
                    className="h-12 w-12 text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`remove-extra-car-${idx}`}
                    aria-label="Remove this car"
                  >
                    <Trash2 className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            );
          })}

          {/* Batch disclosure (only visible once row 2+ exists) */}
          {extraCars.length > 0 && (
            <div
              className="mt-5 bg-amber-50 border-l-[4px] border-amber-500 rounded-r-md px-4 py-3 flex items-start gap-3"
              data-testid="batch-disclosure"
            >
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">
                <span className="font-semibold">Please note:</span> Only add railcars that share the same scope of work,
                location, and last contents as the first car on this form. Onsite Rail Solutions is not responsible for
                inaccurate or mismatched service request data submitted using batch creation. Always verify each car before submitting.
              </p>
            </div>
          )}

          {/* Add another car button */}
          <div className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={addExtraCar}
              disabled={extraCars.length >= 8}
              data-testid="add-car-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Another Car
            </Button>
            {extraCars.length >= 8 && (
              <span className="ml-3 text-xs text-[#808080]">Maximum of 9 cars reached.</span>
            )}
          </div>
        </div>

        {/* Section 1: General (without Car Mark/Number) */}
        <Card className="form-section">
          <CardHeader className="pb-4">
            <CardTitle className="form-section-title">1. General Information</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Service Type *</Label>
              {/* 2026-07-05 Wave 3 #2: dropdown, alphabetical, no default. */}
              <Select
                value={formData.service_type}
                onValueChange={(value) => handleChange('service_type', value)}
              >
                <SelectTrigger data-testid="service-type-select">
                  <SelectValue placeholder="Select service type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="HOME_SHOP">Home Shop</SelectItem>
                  <SelectItem value="MINI_SHOP">Mini Shop</SelectItem>
                  <SelectItem value="MOBILE_REQUEST">Mobile Request</SelectItem>
                  <SelectItem value="ONSITE">Onsite</SelectItem>
                  {/* 2026-07-07 — Same Day Priority, alphabetical between Onsite and Tank Qualification. */}
                  <SelectItem value="SAME_DAY_PRIORITY">Same Day Priority</SelectItem>
                  <SelectItem value="TANK_QUALIFICATION">Tank Qualification</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="facility_id">Responding Facility *</Label>
              <Select
                value={formData.facility_id}
                onValueChange={(value) => handleChange('facility_id', value)}
              >
                <SelectTrigger data-testid="facility-select">
                  <SelectValue placeholder="Select facility" />
                </SelectTrigger>
                <SelectContent>
                  {facilities.map((facility) => (
                    <SelectItem key={facility.id} value={facility.id}>
                      {facility.name}
                      {!facility.facility_code ? ' — (no Facility ID set)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(() => {
                const f = facilities.find(x => x.id === formData.facility_id);
                if (f && !f.facility_code) {
                  return (
                    <div
                      className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800"
                      data-testid="facility-code-missing-banner"
                    >
                      A Facility ID is required before creating service requests.{' '}
                      <Link to="/facility-management" className="font-medium underline">
                        Set it in Facility Management →
                      </Link>
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            <div className="space-y-2">
              <Label htmlFor="call_date">Call Date *</Label>
              <Input
                id="call_date"
                type="date"
                value={formData.call_date}
                max={TODAY_ISO}
                onChange={(e) => handleChange('call_date', e.target.value)}
                required
                data-testid="call-date-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Car Information */}
        <Card className="form-section">
          <CardHeader className="pb-4">
            <CardTitle className="form-section-title">2. Car Information</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="car_owner">Car Owner *</Label>
              <Input
                id="car_owner"
                value={formData.car_owner}
                onChange={(e) => handleChange('car_owner', e.target.value)}
                required
                data-testid="car-owner-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="car_lessee">Car Lessee</Label>
              <Input
                id="car_lessee"
                value={formData.car_lessee}
                onChange={(e) => handleChange('car_lessee', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="se_owner">S.E. Owner</Label>
              <Input
                id="se_owner"
                value={formData.se_owner}
                onChange={(e) => handleChange('se_owner', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="car_type">Car Type *</Label>
              <Select
                value={formData.car_type}
                onValueChange={(value) => handleChange('car_type', value)}
              >
                <SelectTrigger data-testid="car-type-select">
                  <SelectValue placeholder="Select car type" />
                </SelectTrigger>
                <SelectContent>
                  {carTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.car_type === 'Other Freight' && (
              <div className="space-y-2">
                <Label htmlFor="car_type_other">Specify Car Type *</Label>
                <Input
                  id="car_type_other"
                  value={formData.car_type_other}
                  onChange={(e) => handleChange('car_type_other', e.target.value)}
                  required
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="lining_owner">Lining Owner</Label>
              <Input
                id="lining_owner"
                value={formData.lining_owner}
                onChange={(e) => handleChange('lining_owner', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lining_type">Lining Type</Label>
              <Input
                id="lining_type"
                value={formData.lining_type}
                onChange={(e) => handleChange('lining_type', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Jacketed *</Label>
              <RadioGroup
                value={formData.jacketed ? 'yes' : 'no'}
                onValueChange={(value) => handleChange('jacketed', value === 'yes')}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="jacketed_no" />
                  <Label htmlFor="jacketed_no" className="cursor-pointer">No</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="jacketed_yes" />
                  <Label htmlFor="jacketed_yes" className="cursor-pointer">Yes</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="insulation_type">Insulation Type</Label>
              <Input
                id="insulation_type"
                value={formData.insulation_type}
                onChange={(e) => handleChange('insulation_type', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Load Status *</Label>
              <RadioGroup
                value={formData.interior_condition}
                onValueChange={(value) => handleChange('interior_condition', value)}
                className="flex flex-wrap gap-4"
              >
                {[
                  { value: 'CLEAN',   label: 'Clean' },
                  { value: 'EMPTY',   label: 'Empty' },
                  { value: 'RESIDUE', label: 'Residue' },
                  { value: 'LOADED',  label: 'Loaded' },
                  { value: 'OTHER',   label: 'Other' },
                ].map(({ value, label }) => (
                  <div key={value} className="flex items-center space-x-2">
                    <RadioGroupItem
                      value={value}
                      id={`condition_${value}`}
                      data-testid={`load-status-${value.toLowerCase()}`}
                    />
                    <Label htmlFor={`condition_${value}`} className="cursor-pointer">
                      {label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {formData.interior_condition === 'OTHER' && (
              <div className="space-y-2">
                <Label htmlFor="interior_condition_other">Specify Load Status *</Label>
                <Input
                  id="interior_condition_other"
                  value={formData.interior_condition_other}
                  onChange={(e) => handleChange('interior_condition_other', e.target.value)}
                  required
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="last_contents">Last Contents *</Label>
              <Input
                id="last_contents"
                value={formData.last_contents}
                onChange={(e) => handleChange('last_contents', e.target.value)}
                required
                data-testid="last-contents-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="car_owner_contact">Car Owner Contact (Email) *</Label>
              <Input
                id="car_owner_contact"
                type="email"
                value={formData.car_owner_contact}
                onChange={(e) => handleChange('car_owner_contact', e.target.value)}
                required
                data-testid="car-owner-contact-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Section 3: Scope of Work */}
        <Card className="form-section">
          <CardHeader className="pb-4">
            <CardTitle className="form-section-title">3. Scope of Work</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="event_type">Event Type *</Label>
                <EventTypeCombobox
                  options={eventTypes}
                  value={formData.event_type}
                  onChange={(value) => handleChange('event_type', value)}
                  testId="event-type-select"
                  triggerId="event_type"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="work_procedures">Work Procedures to Use *</Label>
                <Input
                  id="work_procedures"
                  value={formData.work_procedures}
                  onChange={(e) => handleChange('work_procedures', e.target.value)}
                  maxLength={2000}
                  required
                  data-testid="work-procedures-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="shopping_reason">Shopping Reason *</Label>
              <Textarea
                id="shopping_reason"
                value={formData.shopping_reason}
                onChange={(e) => handleChange('shopping_reason', e.target.value)}
                placeholder="Describe the reason for this service request..."
                rows={5}
                maxLength={2000}
                required
                data-testid="shopping-reason-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Section 4: Gasket Specifications */}
        <Card className="form-section">
          <CardHeader className="pb-4">
            <CardTitle className="form-section-title">4. Gasket Specifications (Optional)</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="gasket_manway_cover">Manway Cover</Label>
              <Input
                id="gasket_manway_cover"
                value={formData.gasket_manway_cover}
                onChange={(e) => handleChange('gasket_manway_cover', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="gasket_flanged_valves">Flanged Valves</Label>
              <Input
                id="gasket_flanged_valves"
                value={formData.gasket_flanged_valves}
                onChange={(e) => handleChange('gasket_flanged_valves', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="gasket_bov_flange">BOV Flange</Label>
              <Input
                id="gasket_bov_flange"
                value={formData.gasket_bov_flange}
                onChange={(e) => handleChange('gasket_bov_flange', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="gasket_bov_cap_material">BOV Cap Material</Label>
              <Input
                id="gasket_bov_cap_material"
                value={formData.gasket_bov_cap_material}
                onChange={(e) => handleChange('gasket_bov_cap_material', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="gasket_oring_spec">O-ring Specification</Label>
              <Input
                id="gasket_oring_spec"
                value={formData.gasket_oring_spec}
                onChange={(e) => handleChange('gasket_oring_spec', e.target.value)}
              />
            </div>

            <div className="flex items-center space-x-2 pt-6">
              <Checkbox
                id="gasket_food_grade"
                checked={formData.gasket_food_grade}
                onCheckedChange={(checked) => handleChange('gasket_food_grade', checked)}
              />
              <Label htmlFor="gasket_food_grade" className="cursor-pointer">Food Grade</Label>
            </div>
          </CardContent>
        </Card>

        {/* Section 5: Car Location */}
        <Card className="form-section">
          <CardHeader className="pb-4">
            <CardTitle className="form-section-title">5. Car Location</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="saved_contact">Saved Contact</Label>
              <SavedContactCombobox
                key={savedContactKey}
                triggerId="saved_contact"
                testId="repair-site-dropdown"
                options={repairSiteContacts}
                primary={(s) => s.repair_site_name}
                secondary={(s) => {
                  const loc = `${s.city || ''}${s.state_province ? `, ${s.state_province}` : ''}`;
                  return s.contact_type === 'DEDICATED_SITE' ? `Dedicated Site${loc ? ' • ' + loc : ''}` : loc;
                }}
                onSelect={(id) => handleRepairSiteSelect(id)}
                placeholder="Choose a saved Repair Site or Dedicated Site to autofill below…"
                emptyMessage="No saved Repair Sites or Dedicated Sites"
                searchPlaceholder="Search by site name or city…"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="site_name">Repair Site *</Label>
              <Input
                id="site_name"
                value={formData.site_name}
                onChange={(e) => handleChange('site_name', e.target.value)}
                required
                data-testid="site-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="splc_code">SPLC Code</Label>
              <Input
                id="splc_code"
                value={formData.splc_code}
                onChange={(e) => handleChange('splc_code', e.target.value.replace(/\D/g, ''))}
                placeholder="6-8 digits"
                maxLength={8}
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="location_address">Address</Label>
              <Input
                id="location_address"
                value={formData.location_address}
                onChange={(e) => handleChange('location_address', e.target.value)}
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="location_address_2">Address Line 2</Label>
              <Input
                id="location_address_2"
                value={formData.location_address_2}
                onChange={(e) => handleChange('location_address_2', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="location_city">City</Label>
              <Input
                id="location_city"
                value={formData.location_city}
                onChange={(e) => handleChange('location_city', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="location_state">State/Province</Label>
              <Input
                id="location_state"
                value={formData.location_state}
                onChange={(e) => handleChange('location_state', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="location_zip">ZIP Code</Label>
              <Input
                id="location_zip"
                value={formData.location_zip}
                onChange={(e) => handleChange('location_zip', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="site_contact">Site Contact</Label>
              <Input
                id="site_contact"
                value={formData.site_contact}
                onChange={(e) => handleChange('site_contact', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="site_contact_number">Site Contact Number</Label>
              <Input
                id="site_contact_number"
                value={formData.site_contact_number}
                onChange={(e) => handleChange('site_contact_number', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="site_contact_email">Site Contact Email</Label>
              <Input
                id="site_contact_email"
                type="email"
                value={formData.site_contact_email}
                onChange={(e) => handleChange('site_contact_email', e.target.value)}
              />
            </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 6: Service Requestor */}
        <Card className="form-section">
          <CardHeader className="pb-4">
            <CardTitle className="form-section-title">6. Service Requestor</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="saved_requestor">Saved Service Requestor</Label>
              <SavedContactCombobox
                key={savedRequestorKey}
                triggerId="saved_requestor"
                testId="saved-requestor-dropdown"
                options={requestorContacts}
                primary={(c) => c.requestor_full_name}
                secondary={(c) => c.requestor_company}
                onSelect={(id) => handleRequestorSelect(id)}
                placeholder="Choose a saved Service Requestor to autofill below…"
                emptyMessage="No saved Service Requestors yet"
                searchPlaceholder="Search by name or company…"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="requestor_full_name">Full Name</Label>
              <Input
                id="requestor_full_name"
                value={formData.requestor_full_name}
                onChange={(e) => handleChange('requestor_full_name', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="requestor_company">Company *</Label>
              <Input
                id="requestor_company"
                value={formData.requestor_company}
                onChange={(e) => handleChange('requestor_company', e.target.value)}
                required
                data-testid="requestor-company-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="requestor_phone">Phone</Label>
              <Input
                id="requestor_phone"
                value={formData.requestor_phone}
                onChange={(e) => handleChange('requestor_phone', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="requestor_email">Email</Label>
              <Input
                id="requestor_email"
                type="email"
                value={formData.requestor_email}
                onChange={(e) => handleChange('requestor_email', e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Section 7: Billing Party */}
        <Card className="form-section">
          <CardHeader className="pb-4">
            <CardTitle className="form-section-title">7. Responsible Billing Party</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="saved_billing">Saved Responsible Billing Party</Label>
              <SavedContactCombobox
                key={savedBillingKey}
                triggerId="saved_billing"
                testId="saved-billing-dropdown"
                options={billingContacts}
                primary={(c) => c.billing_company}
                secondary={(c) => c.billing_contact_name}
                onSelect={(id) => handleBillingSelect(id)}
                placeholder="Choose a saved Responsible Billing Party to autofill below…"
                emptyMessage="No saved Responsible Billing Parties yet"
                searchPlaceholder="Search by company or contact…"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="billing_company">Company</Label>
              <Input
                id="billing_company"
                value={formData.billing_company}
                onChange={(e) => handleChange('billing_company', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="billing_contact_name">Primary Contact Name</Label>
              <Input
                id="billing_contact_name"
                value={formData.billing_contact_name}
                onChange={(e) => handleChange('billing_contact_name', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="billing_contact_email">Primary Contact Email</Label>
              <Input
                id="billing_contact_email"
                type="email"
                value={formData.billing_contact_email}
                onChange={(e) => handleChange('billing_contact_email', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="billing_contact_phone">Phone</Label>
              <Input
                id="billing_contact_phone"
                value={formData.billing_contact_phone}
                onChange={(e) => handleChange('billing_contact_phone', e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end gap-4 pb-8">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/service-requests')}
            data-testid="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
            disabled={loading}
            data-testid="save-service-request-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Service Request
              </>
            )}
          </Button>
        </div>
      </form>

      {/* 2026-06-05 — Duplicate SR confirmation modal. Warns but never blocks.
          The user can Continue (create anyway) or Cancel back to the form. */}
      <Dialog open={dupModalOpen} onOpenChange={setDupModalOpen}>
        <DialogContent data-testid="duplicate-sr-modal">
          <DialogHeader>
            <DialogTitle>Possible duplicate service request</DialogTitle>
            <DialogDescription className="text-black text-base leading-relaxed">
              {dupMatch ? (
                <>
                  A service request already exists for{' '}
                  <b>{dupMatch.car_mark} {dupMatch.car_number}</b> at{' '}
                  <b>{dupMatch.repair_site || dupMatch.site_name}</b> with event type{' '}
                  <b>{dupMatch.event_type}</b>. Do you want to continue creating a new service request?
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => { setDupModalOpen(false); setDupMatch(null); }}
              data-testid="dup-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                const { allCars } = validateCarRows();
                performCreate(allCars);
              }}
              className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
              data-testid="dup-continue-btn"
            >
              Continue Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddServiceRequestPage;
